
import {map} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { LoggerService } from '../logger-service/logger.service';
import { StorageService } from '../storage-service/storage.service';
import { DialogService } from '../confirm-dialog/dialog.service';

import { ApplicationService } from '../application-service/application.service';
import { THIS_EXPR } from '@angular/compiler/src/output/output_ast';
import { SessionTimeoutService } from '../session-service/sessiontimeoutservice'
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class AuthenticationService {
    private _protocol: string = window.location.protocol;
    private _baseApi: string = '/api/mgt/1.0';
    private refreshTimer: NodeJS.Timer;
    private expiredTimer: NodeJS.Timer;
    private originalTokenValidPeriod: number | null = null;
    private windowStartTime: number | null = null;
    private windowDuration: number | null = null;
    private apiCallInWindow: boolean = false;
    private windowTimer: any = null;
    private isLoginInProgress: boolean = false; 
    private hasInitializedWindowAfterReload: boolean = false;
    private isPageRefreshInProgress: boolean = false;


    constructor(
        private _http: HttpClient,
        private _helper: JwtHelperService,
        private _logger: LoggerService,
        private _storage: StorageService,
        private _dialogService: DialogService,
        private _router: Router,
        private _application: ApplicationService,
        private _sessionTimeout: SessionTimeoutService
    ) { }

    //Login with Basic Authentication method
    public login(username: string, password: string, backend: string): void {
        this.isLoginInProgress = true; 
        //console.log('login', backend);
        const url = `${this._protocol}//${backend || document.location.hostname}${this._baseApi}/login`;
        //console.log('urllogin', url);
        this._logger.info('AuthenticationService', 'Logging to: ' + url);
      
        const headers = new HttpHeaders({
          'Authorization': 'Basic ' + btoa(`${username}:${password}`),
          'Accept': 'application/json'
        });
      
        this._http.get<any>(url, { headers }).subscribe(
          data => {
            if (data.token) {
              this.useAuthToken(data.token);
              this.updateRefreshWindow(data.token);
              this._sessionTimeout.setupIdleLogout();
      
              if (data.last_login) {
                const theDate = new Date(data.last_login.stamp * 1000);
                const theDateToDisplay = `${theDate.toLocaleDateString(navigator.language)} ${theDate.toLocaleTimeString(navigator.language)}`;
                this.saveLastLogin(theDateToDisplay);
              }
      
              if (data.hostname) {
                this.saveHostName(data.hostname);
              }
      
              this._storage.storeUsername(username);
              this._storage.storeBackend(backend);
      
              this._router.navigate(['home']);
              
              setTimeout(() => {
                this.isLoginInProgress = false;
                //this._logger.log('AuthenticationService', 'Login initialization completed');
              }, 3000); 
            } else {
              this._logger.error('AuthenticationService', 'No token retrieved from server!');
              this._dialogService.alert('auth-error-message', 'auth-failed');
              this.isLoginInProgress = false; 
            }
          },
          error => {
            this._logger.error('AuthenticationService', error);
            this._dialogService.alert('auth-error-message', 'auth-failed');
            this.isLoginInProgress = false; 
          }
        );
      }
      

    public logout() {
        console.log('[AuthenticationService] Logging out user');
        this.removeAuthToken();
        this.reload();
    }

    private reload() {
        window.location.reload();
    }

    public reset() {
        this.removeAuthToken();
    }

    public isLoggedIn() {
        return this._storage.retrieveToken() != null && !this.isTokenExpired(this._storage.retrieveToken());
    }

    public routeToLogin() {
        let route: string = this._storage.retrieveBackend() ? 'login-dev' : 'login';
        this._router.navigate([route]);
    }

    private useAuthToken(token: any) {
        if (token) {
            this._storage.storeToken(token);
        }
    }

    private saveLastLogin(loginDate: any)
    {
        if (loginDate) {
            this._storage.storeLoginDate(loginDate);

        }
    }
    private saveHostName(hostName: any)
    {
        if (hostName) {
            this._storage.storeHostName(hostName);

        }
    }

    private removeAuthToken() {
        clearTimeout(this.refreshTimer);
        clearTimeout(this.expiredTimer);
        this._storage.removeToken();
        this._storage.removeLoginDate();
        this._storage.removeHostName();
        this.isPageRefreshInProgress = false;
        this.isLoginInProgress = false;
        this.hasInitializedWindowAfterReload = false;
    }

    
    public refreshAuthToken(backend?: string): Observable<any> {
      const headers = new HttpHeaders().set('Accept', 'application/json');
      //console.log('refreshAuthToken', backend);
      const url = `${this._protocol}//${backend || document.location.hostname}${this._baseApi}/refresh-token`;
      
      //console.log('url', url);
      return this._http.get(url, { headers }).pipe(
        map((data: any) => {
            this._logger.log('AuthenticationService', 'Refresh token success');
            if (data && data.token) {
                this._storage.storeToken(data.token);
                this.updateRefreshWindow(data.token);
            }
            return data;
        }),
        catchError(error => {
            this._logger.error('AuthenticationService', 'ERROR refresh Token : ' + JSON.stringify(error));
            return throwError(error);
        })
      );
    }
    

    private scheduleAuthTokenRefresh(token: string): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }

        const expirationDate = this._helper.getTokenExpirationDate(token);
        const decoded = this._helper.decodeToken(token);
        const issuedAt = decoded?.iat ? new Date(decoded.iat * 1000) : new Date();
        const tokenValidPeriod = expirationDate.getTime() - issuedAt.getTime();

        if (this._sessionTimeout.setIdleTimeout) {
            this._sessionTimeout.setIdleTimeout(tokenValidPeriod / 1000);
        }
        if (this._sessionTimeout.resetTimer) {
            this._sessionTimeout.resetTimer();
        }

        const interval = tokenValidPeriod * 0.10;
        this._logger.log('AuthenticationService', 'Scheduling next refresh in: ' + (interval / 1000) + 's');

        this.refreshTimer = setTimeout(() => {
            //this._logger.log('AuthenticationService', 'Triggering refresh token at interval: ' + interval);
            this.refreshAuthToken(this._storage.retrieveBackend());
        }, interval);
    }

    private getTokenIssuedAtDate(token: string): Date {
        let decoded: any = this._helper.decodeToken(token);

        if (!decoded.hasOwnProperty('iat')) {
            return null;
        }

        let date = new Date(0); 
        date.setUTCSeconds(decoded.iat);

        return date;
    }

    public isTokenExpired(token: string, offsetSeconds?: number): boolean {
        let expdate = this._helper.getTokenExpirationDate(token);
        offsetSeconds = offsetSeconds || 0;

        if (expdate == null) {
            return false;
        }

        let issuedAtDate = this.getTokenIssuedAtDate(token);

        if (issuedAtDate == null) {
            issuedAtDate = new Date();
        }

        // Token expired?
        return !(expdate.valueOf() > (issuedAtDate.valueOf() + (offsetSeconds * 1000)));
    }

    public updateRefreshWindow(token: string): void {
        const expirationDate = this._helper.getTokenExpirationDate(token);
        const decoded = this._helper.decodeToken(token);
        const issuedAt = decoded?.iat ? new Date(decoded.iat * 1000) : new Date();
        const tokenValidPeriod = expirationDate.getTime() - issuedAt.getTime();
        this.windowStartTime = Date.now();
        this.windowDuration = tokenValidPeriod * 0.10;
        this.apiCallInWindow = false;
        this._logger.log('AuthenticationService', `Refresh window set for ${(this.windowDuration / 1000).toFixed(2)} seconds`);
        if (this.windowTimer) {
            clearTimeout(this.windowTimer);
        }
        this.windowTimer = setTimeout(() => {
            this._logger.log('AuthenticationService', `Refresh window ended after ${(this.windowDuration / 1000).toFixed(2)} seconds. apiCallInWindow=${this.apiCallInWindow}`);
            if (this.apiCallInWindow) {
                this.refreshAuthToken(this._storage.retrieveBackend()).subscribe();
            } 
        }, this.windowDuration);
    }

    public onApiCall(): void {
        if (!this.isLoginInProgress && !this.isPageRefreshInProgress) {
            this.apiCallInWindow = true;
            //this._logger.log('AuthenticationService', 'User-initiated API call detected');
        } else {
            const context = this.isLoginInProgress ? 'login initialization' : 'page refresh initialization';
            //this._logger.log('AuthenticationService', `API call during ${context} - ignoring for refresh logic`);
        }
    }

    public shouldRefreshOnApiCall(): boolean {
        if (this.windowStartTime === null || this.windowDuration === null) {
            return true; 
        }
        return Date.now() >= this.windowStartTime + this.windowDuration;
    }
    public initializeWindowIfTokenExists(): void {
        if (this.hasInitializedWindowAfterReload) {
            return;
        }
        const token = this._storage.retrieveToken();
        if (token && !this.isTokenExpired(token)) {
            this.isPageRefreshInProgress = true;
            this.updateRefreshWindow(token);
            
            // Set a timeout to allow auto API calls during page refresh to complete
            setTimeout(() => {
                this.isPageRefreshInProgress = false;
               // this._logger.log('AuthenticationService', 'Page refresh initialization completed');
            }, 3000);
        }
        this.hasInitializedWindowAfterReload = true;
    }

}





